import React, { useCallback, useMemo } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import { hoveredNodeIdAtom, selectedNodeIdAtom, templateEdgesAtom, templateNodesAtom } from '../../../../labelling-v2/atoms/skeleton-template-builder/template-atoms';
import { templateDispatchAtom } from '../../../../labelling-v2/atoms/skeleton-template-builder/template-dispatch';
import { useTemplateCanvasInteractions } from '../../../../labelling-v2/hooks/skeleton-template-builder/use-template-canvas-interactions';
import { EVENTS } from '../../../../labelling-v2/types/skeleton-template-builder.type';
import { useCoreStageContext } from '../../../providers/core-stage-provider/context';
import { CanvasSvgLayer } from './canvas-svg-layer';
import { NodeLabel } from './node-label';
import { NodeNameInput } from './node-name-input';
export function TemplateCanvasContent() {
  const [css, theme] = useStyletron();
  const {
    getStageDimension
  } = useCoreStageContext();
  const nodes = useAtomValue(templateNodesAtom);
  const edges = useAtomValue(templateEdgesAtom);
  const hoveredNodeId = useAtomValue(hoveredNodeIdAtom);
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const dispatch = useSetAtom(templateDispatchAtom);
  const setHoveredNodeId = useSetAtom(hoveredNodeIdAtom);
  const {
    pendingNode,
    edgePreview,
    onConfirmNode,
    onCancelNode
  } = useTemplateCanvasInteractions();
  const canvasDimensions = getStageDimension();
  const nodeMap = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const handleConfirmNode = useCallback((label: string) => onConfirmNode(label, theme.colors.accent), [onConfirmNode, theme.colors.accent]);
  const handleNodeSelect = useCallback((nodeId: string) => dispatch({
    type: EVENTS.SELECT_NODE,
    nodeId
  }), [dispatch]);
  const handleNodeHoverChange = useCallback((nodeId: string | null) => setHoveredNodeId(nodeId), [setHoveredNodeId]);
  return <>
      {nodes.length === 0 && !pendingNode && <div className={css({
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      ...theme.typography.ParagraphMedium,
      color: theme.colors.contentTertiary,
      pointerEvents: 'none',
      userSelect: 'none'
    })}>
          Click anywhere to create a key-point
        </div>}

      <CanvasSvgLayer data={{
      nodes,
      edges,
      nodeMap
    }} preview={{
      pendingNode,
      edgePreview
    }} selection={{
      hoveredNodeId,
      selectedNodeId
    }} handlers={{
      onNodeSelect: handleNodeSelect,
      onNodeHoverChange: handleNodeHoverChange
    }} strokeColor={theme.colors.accent} />

      {nodes.map(node => {
      const isHighlighted = hoveredNodeId === node.id || selectedNodeId === node.id;
      return <NodeLabel key={`label-${node.id}`} x={node.x} y={node.y} label={node.label} highlighted={isHighlighted} canvasDimensions={canvasDimensions} />;
    })}

      {pendingNode && <NodeNameInput x={pendingNode.x} y={pendingNode.y} canvasDimensions={canvasDimensions} onConfirm={handleConfirmNode} onCancel={onCancelNode} />}
    </>;
}